import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Img from './Img';
import { SearchIcon } from './Nav';
import { GALLERIES, KIND_LABEL, imagesFor } from '../utils/galleries';
import { useUi } from '../hooks/useUi';
import useScrollLock from '../hooks/useScrollLock';

let index;
const buildIndex = () => {
  if (index) return index;
  const galleries = GALLERIES.map((g) => {
    const c = g.content;
    return {
      key: g.slug,
      title: g.title,
      meta: `${KIND_LABEL[g.kind]} · ${imagesFor(g.slug).length}`,
      path: g.path,
      haystack: [g.title, g.blurb, c.intro, c.essay, ...(c.keywords || [])].join(' ').toLowerCase(),
    };
  });
  const photos = GALLERIES.flatMap((g) =>
    imagesFor(g.slug)
      .filter((img) => img.caption)
      .map((img) => ({
        key: `${g.slug}/${img.stem}`,
        title: img.caption,
        meta: g.title,
        path: `${g.path}?photo=${encodeURIComponent(img.stem)}`,
        image: img,
        haystack: `${img.caption} ${g.title}`.toLowerCase(),
      }))
  );
  index = { galleries, photos };
  return index;
};

const matches = (item, tokens) => tokens.every((t) => item.haystack.includes(t));

/** Full-screen search over galleries, their writing, and photo captions. */
const Search = () => {
  const ui = useUi();
  const { searchOpen, openSearch, closeSearch } = ui;
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useScrollLock(searchOpen);

  // "/" or Ctrl/Cmd+K opens search from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      const typing = e.target.closest?.('input, textarea, [contenteditable]');
      if ((e.key === '/' && !typing) || (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openSearch]);

  useEffect(() => {
    if (!searchOpen) return;
    setQuery('');
    setActive(0);
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [searchOpen]);

  const results = useMemo(() => {
    const { galleries, photos } = buildIndex();
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) {
      return [{ label: 'Recent trips', items: galleries.filter((g) => GALLERIES.find((x) => x.slug === g.key).kind === 'trip').slice(0, 3) }];
    }
    const g = galleries
      .filter((item) => matches(item, tokens))
      .sort((a, b) => Number(b.title.toLowerCase().startsWith(tokens[0])) - Number(a.title.toLowerCase().startsWith(tokens[0])));
    const p = photos.filter((item) => matches(item, tokens)).slice(0, 8);
    return [
      { label: 'Galleries', items: g },
      { label: 'Photographs', items: p },
    ].filter((group) => group.items.length);
  }, [query]);

  const flat = results.flatMap((group) => group.items);

  const go = (path) => {
    closeSearch();
    navigate(path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeSearch();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(flat.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter' && flat[active]) {
      e.preventDefault();
      go(flat[active].path);
    }
  };

  useEffect(() => {
    listRef.current?.querySelector('[data-active]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const counts = {
    gallery: GALLERIES.filter((g) => g.kind === 'gallery').length,
    trip: GALLERIES.filter((g) => g.kind === 'trip').length,
  };

  let cursor = -1;

  return (
    <div
      className={`search${searchOpen ? ' is-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      aria-hidden={!searchOpen}
      inert={searchOpen ? undefined : ''}
      onKeyDown={onKeyDown}
    >
      <div className="search__bar">
        <span className="search__brand">Jonny Coe</span>
        <span className="search__barRight">
          <span className="search__esc">Esc to close</span>
          <button type="button" className="search__close" aria-label="Close search" onClick={closeSearch}>
            <span />
          </button>
        </span>
      </div>

      <div className="search__wrap">
        <label className="search__field">
          <SearchIcon size={22} />
          <input
            ref={inputRef}
            className="search__input"
            type="search"
            placeholder="Search galleries, trips, photos"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            aria-controls="search-results"
            aria-activedescendant={flat[active] ? `sr-${flat[active].key}` : undefined}
            autoComplete="off"
            spellCheck="false"
          />
        </label>

        <div className="search__content" id="search-results" ref={listRef} role="listbox">
          {results.map((group) => (
            <div key={group.label} className="search__group">
              <span className="label">{group.label}</span>
              {group.items.map((item) => {
                cursor += 1;
                const isActive = cursor === active;
                const i = cursor;
                return (
                  <a
                    key={item.key}
                    id={`sr-${item.key}`}
                    href={item.path}
                    role="option"
                    aria-selected={isActive}
                    className="search__item"
                    data-active={isActive ? '' : undefined}
                    onMouseEnter={() => setActive(i)}
                    onClick={(e) => {
                      e.preventDefault();
                      go(item.path);
                    }}
                  >
                    <span className="search__arrow" aria-hidden="true">
                      →
                    </span>
                    {item.image && (
                      <span className="search__thumb" style={{ '--a': item.image.aspect }}>
                        <Img image={item.image} alt="" sizes="80px" />
                      </span>
                    )}
                    <span className="search__itemTitle">{item.title}</span>
                    <span className="search__itemMeta">{item.meta}</span>
                  </a>
                );
              })}
            </div>
          ))}
          {query && !flat.length && <p className="search__empty">Nothing matches “{query}”.</p>}

          {!query && (
            <div className="search__group">
              <span className="label">Browse by type</span>
              <div className="search__types">
                {[
                  ['gallery', 'Galleries'],
                  ['trip', 'Trips'],
                ].map(([type, name]) => (
                  <button key={type} type="button" className="search__type" onClick={() => go(`/work?type=${type}`)}>
                    <span className="search__typeName">{name}</span>
                    <span className="search__typeCount">{counts[type]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="search__legend" aria-hidden="true">
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd>
          <b>Navigate</b>
        </span>
        <span>
          <kbd>↵</kbd>
          <b>Open</b>
        </span>
        <span>
          <kbd>esc</kbd>
          <b>Close</b>
        </span>
      </div>
    </div>
  );
};

export default Search;
